import { useEffect, useRef, useState } from "react";
import { Upload, FileSpreadsheet, FileText, CheckCircle2, AlertTriangle, Loader2, Trash2, RefreshCw } from "lucide-react";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import api from "../../lib/api";
import type { BankImport } from "../../types/bank";

interface ImportWizardProps {
  onImported: () => void;
}

type Step = "upload" | "result";

interface SkippedDuplicate {
  date: string;
  amount: number;
  counterparty: string | null;
  description: string;
  reason: "exact" | "near";
}

interface ImportResult {
  importId: string;
  status: string;
  transactionCount: number;
  duplicateCount: number;
  duplicatesSkipped: SkippedDuplicate[];
  warnings: string[];
  detectedColumns: Record<string, string>;
}

const columnLabels: Record<string, string> = {
  date: "Datum",
  amount: "Bedrag",
  counterparty: "Tegenpartij",
  description: "Omschrijving",
  balance: "Saldo",
  debitCredit: "Af/Bij",
  reference: "Referentie",
  currency: "Valuta",
};

const bankOptions = [
  { value: "", label: "Automatisch herkennen" },
  { value: "RABOBANK", label: "Rabobank" },
  { value: "ING", label: "ING" },
  { value: "ABN_AMRO", label: "ABN AMRO" },
  { value: "BUNQ", label: "Bunq" },
  { value: "GENERIC", label: "Overige bank (generiek)" },
];

/** 3-staps import-wizard: upload → (poll bij PDF) → resultaat + historie. */
export function ImportWizard({ onImported }: ImportWizardProps) {
  const [step, setStep] = useState<Step>("upload");
  const [bankHint, setBankHint] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [imports, setImports] = useState<BankImport[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadImports = () =>
    api
      .get("/api/bank-transactions/imports")
      .then((res) => setImports(res.data ?? []))
      .catch(() => {});

  useEffect(() => {
    loadImports();
  }, []);

  // Poll zolang er nog een PDF import PARSING is.
  useEffect(() => {
    if (!imports.some((i) => i.status === "PARSING")) return;
    const t = setInterval(loadImports, 3000);
    return () => clearInterval(t);
  }, [imports]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (bankHint) fd.append("bankHint", bankHint);
      const { data } = await api.post("/api/bank-transactions/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setStep("result");
      setFile(null);
      loadImports();
      onImported();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Import mislukt. Controleer het bestand en probeer opnieuw.");
    } finally {
      setUploading(false);
    }
  };

  const undoImport = async (id: string) => {
    if (!confirm("Deze import ongedaan maken? Niet-gekoppelde transacties worden verwijderd.")) return;
    try {
      await api.delete(`/api/bank-transactions/imports/${id}`);
      loadImports();
      onImported();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Ongedaan maken mislukt.");
    }
  };

  return (
    <div className="space-y-6">
      {step === "upload" && (
        <div className="surface-card p-6 space-y-4">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) setFile(f);
            }}
            className="border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center cursor-pointer hover:border-brand-400 hover:bg-brand-50/30 transition-colors"
            onClick={() => fileRef.current?.click()}
          >
            <Upload size={40} className="mx-auto text-slate-400 mb-3" />
            <p className="font-semibold text-slate-700">{file ? file.name : "Klik of sleep een bankafschrift hierheen"}</p>
            <p className="text-sm text-slate-400 mt-1">CSV (Rabobank, ING, ABN AMRO of generiek) of PDF (OCR leest het afschrift)</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>

          <div className="max-w-xs">
            <Select label="Bank" options={bankOptions} value={bankHint} onChange={(e) => setBankHint(e.target.value)} />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}

          <div className="flex justify-end">
            <Button onClick={upload} loading={uploading} disabled={!file}>
              <FileSpreadsheet size={18} /> Importeren
            </Button>
          </div>
        </div>
      )}

      {step === "result" && result && (
        <div className="surface-card p-6 space-y-3">
          <div className="flex items-center gap-3">
            {result.status === "COMPLETED" ? (
              <CheckCircle2 size={28} className="text-emerald-600" />
            ) : result.status === "FAILED" ? (
              <AlertTriangle size={28} className="text-red-600" />
            ) : (
              <Loader2 size={28} className="text-brand-600 animate-spin" />
            )}
            <div>
              <p className="font-bold text-slate-800">
                {result.status === "COMPLETED" ? "Import voltooid" : result.status === "FAILED" ? "Import mislukt" : "PDF wordt gelezen (OCR)…"}
              </p>
              {result.status === "COMPLETED" && (
                <p className="text-sm text-slate-500">
                  {result.transactionCount} nieuwe transactie(s) · {result.duplicateCount} dubbel overgeslagen
                </p>
              )}
            </div>
          </div>
          {result.status === "COMPLETED" && result.duplicatesSkipped?.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-xl px-4 py-3">
              <p className="text-sm font-semibold text-amber-800 mb-1.5">
                Overgeslagen als dubbel ({result.duplicateCount}):
              </p>
              <ul className="text-xs text-amber-800 space-y-1 max-h-48 overflow-y-auto">
                {result.duplicatesSkipped.map((d, i) => (
                  <li key={i} className="flex gap-2 items-baseline">
                    <span className="font-mono shrink-0">{d.date}</span>
                    <span className={`font-semibold shrink-0 ${d.amount < 0 ? "text-red-700" : "text-emerald-700"}`}>
                      {d.amount < 0 ? "-" : "+"}€ {Math.abs(d.amount).toFixed(2)}
                    </span>
                    <span className="truncate">{d.counterparty || d.description}</span>
                    {d.reason === "near" && (
                      <span className="shrink-0 text-[10px] font-semibold bg-amber-200/70 px-1.5 py-0.5 rounded-full">zelfde dag+bedrag</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.status === "COMPLETED" && result.detectedColumns && Object.keys(result.detectedColumns).length > 0 && (
            <p className="text-xs text-slate-500">
              Herkende kolommen:{" "}
              {Object.entries(result.detectedColumns)
                .map(([k, v]) => `${columnLabels[k] ?? k}="${v}"`)
                .join(" · ")}
            </p>
          )}
          {result.warnings?.length > 0 && (
            <ul className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 space-y-1 list-disc list-inside">
              {result.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
          <div className="flex gap-3 pt-2">
            <Button variant="ghost" onClick={() => { setStep("upload"); setResult(null); }}>
              <RefreshCw size={18} /> Nog een bestand importeren
            </Button>
          </div>
        </div>
      )}

      {imports.length > 0 && (
        <div className="surface-card p-6">
          <h3 className="font-bold text-slate-800 mb-3">Import-historie (laatste 50)</h3>
          <div className="divide-y divide-slate-100">
            {imports.map((imp) => (
              <div key={imp.id} className="py-2.5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  {imp.fileType === "PDF" ? <FileText size={20} className="text-slate-400 shrink-0" /> : <FileSpreadsheet size={20} className="text-slate-400 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-700 truncate">{imp.fileName}</p>
                    <p className="text-xs text-slate-400">
                      {new Date(imp.createdAt).toLocaleString("nl-NL")} · {imp.transactionCount} transacties · {imp.duplicateCount} dubbel
                      {imp.status === "PARSING" && " · wordt gelezen…"}
                      {imp.status === "FAILED" && imp.errorMessage ? ` · fout: ${imp.errorMessage}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {imp.status === "COMPLETED" && (
                    <Badge green>{imp.status === "COMPLETED" ? "Voltooid" : imp.status}</Badge>
                  )}
                  {imp.status === "PARSING" && <Badge amber>Bezig…</Badge>}
                  {imp.status === "FAILED" && <Badge red>Mislukt</Badge>}
                  <Button variant="ghost" size="sm" onClick={() => undoImport(imp.id)} title="Ongedaan maken">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="surface-card p-5 text-sm text-slate-500 space-y-1">
        <p className="font-semibold text-slate-700 text-base mb-2">Hoe werkt het?</p>
        <p>• Dezelfde transactie die twee keer wordt geïmporteerd wordt automatisch overgeslagen (idempotente import).</p>
        <p>• PDF-afschriften worden met OCR gelezen; controleer het resultaat daarna in het tabblad "Transacties".</p>
        <p>• Transacties die al gekoppeld zijn kunnen niet via een import worden verwijderd.</p>
      </div>
    </div>
  );
}

function Badge({ children, green, amber, red }: { children: React.ReactNode; green?: boolean; amber?: boolean; red?: boolean }) {
  const cls = green
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : amber
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : red
    ? "bg-red-50 text-red-700 border-red-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{children}</span>;
}